// src/components/layout/Navbar.tsx
'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useSession, signOut } from 'next-auth/react'
import {
  Menu, X, LogIn, Sparkles, ArrowRight, Crown,
  BookOpen, FlaskConical, KeyRound, PenLine,
  ChevronDown, FileText, Layers, ChevronRight, Video,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '@/components/ui/sheet'

interface NavbarProps {
  variant?: 'marketing' | 'student' | 'admin'
}

interface ResourcePage {
  id: string
  title: string
  slug: string
  navGroup: string | null
}
interface ResourceNavGroup {
  name: string | null
  pages: ResourcePage[]
}
interface ResourceCategory {
  id: string
  name: string
  slug: string
  navGroups: ResourceNavGroup[]
}

interface CourseNavItem {
  id: string
  title: string
  slug: string
  targetExam: string | null
  monthlyPrice: number
}

interface NavbarClientProps {
  variant?: 'marketing' | 'student' | 'admin'
  initialCategories: ResourceCategory[]
  initialPyqCategories: {id: string, name: string, slug: string}[]
  initialCourses?: CourseNavItem[]
}

const NAV_LINKS: { label: string; href: string; icon: React.ReactNode }[] = []

const TEST_SERIES_CATEGORIES = [
  {
    id: 'neet',
    name: 'NEET',
    classes: [
      { name: 'Class 11th', slug: 'class-11' },
      { name: 'Class 12th', slug: 'class-12' },
      { name: 'Class 12th Plus', slug: 'class-12-plus' },
    ]
  },
  {
    id: 'jee',
    name: 'JEE (Main+Advanced)',
    classes: [
      { name: 'Class 11th', slug: 'class-11' },
      { name: 'Class 12th', slug: 'class-12' },
      { name: 'Class 12th Plus', slug: 'class-12-plus' },
    ]
  }
]

// Removed hardcoded PRACTICE_CATEGORIES

export function NavbarClient({ 
  variant = 'marketing', 
  initialCategories = [], 
  initialPyqCategories = [],
  initialCourses = []
}: NavbarClientProps) {
  const [isOpen,         setIsOpen]         = useState(false)
  const [isScrolled,     setIsScrolled]     = useState(false)
  const [showNavbar,     setShowNavbar]     = useState(true)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [categories]     = useState<ResourceCategory[]>(initialCategories)
  const [pyqCategories]  = useState(initialPyqCategories)
  const [coursesList]    = useState<CourseNavItem[]>(initialCourses)

  // Courses Menu State
  const [coursesOpen, setCoursesOpen] = useState(false)
  const coursesRef = useRef<HTMLDivElement>(null)
  const coursesCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // null = menu closed or open but nothing hovered yet
  const [resourcesOpen,    setResourcesOpen]    = useState(false)
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null)
  // vertical offset of the hovered category row relative to the left panel
  const [rightPanelTop,    setRightPanelTop]    = useState(0)

  // Mobile accordion
  const [mobileResOpen,     setMobileResOpen]     = useState(false)
  const [mobileActiveCatId, setMobileActiveCatId] = useState<string | null>(null)

  // Test Series Mega Menu State
  const [tsOpen, setTsOpen] = useState(false)
  const [activeTsId, setActiveTsId] = useState<string | null>(null)
  const [tsRightPanelTop, setTsRightPanelTop] = useState(0)
  const tsRef = useRef<HTMLDivElement>(null)
  const tsLeftPanelRef = useRef<HTMLDivElement>(null)
  const tsCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Mobile TS State
  const [mobileTsOpen, setMobileTsOpen] = useState(false)
  const [mobileActiveTsId, setMobileActiveTsId] = useState<string | null>(null)

  // Practice Mega Menu State
  const [practiceOpen, setPracticeOpen] = useState(false)
  const practiceRef = useRef<HTMLDivElement>(null)
  const practiceCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Mobile Practice State
  const [mobilePracticeOpen, setMobilePracticeOpen] = useState(false)

  // PYQ Mega Menu State
  const [pyqOpen, setPyqOpen] = useState(false)
  const [activePyqId, setActivePyqId] = useState<string | null>(null)
  const [pyqRightPanelTop, setPyqRightPanelTop] = useState(0)
  const pyqRef = useRef<HTMLDivElement>(null)
  const pyqLeftPanelRef = useRef<HTMLDivElement>(null)
  const pyqCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Mobile PYQ State
  const [mobilePyqOpen, setMobilePyqOpen] = useState(false)
  const [mobileActivePyqId, setMobileActivePyqId] = useState<string | null>(null)

  const lastScrollY   = useRef(0)
  const resourcesRef  = useRef<HTMLDivElement>(null)
  const leftPanelRef  = useRef<HTMLDivElement>(null)
  const closeTimer    = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data: session, status } = useSession()

  // Scroll behaviour
  useEffect(() => {
    if (typeof window === 'undefined') return
    const handleScroll = () => {
      const y = window.scrollY
      setIsScrolled(y > 10)
      setShowNavbar(!(y > lastScrollY.current && y > 100))
      const docH = document.documentElement.scrollHeight - window.innerHeight
      setScrollProgress(docH > 0 ? Math.min(y / docH, 1) : 0)
      lastScrollY.current = y
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Fetch resource categories removed - now passed as props

  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setResourcesOpen(true)
  }

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => {
      setResourcesOpen(false)
      setActiveCategoryId(null)
      setRightPanelTop(0)
    }, 150)
  }

  const closeMenu = () => {
    setResourcesOpen(false)
    setActiveCategoryId(null)
    setRightPanelTop(0)
  }

  // When mouse enters a category row, measure its top offset relative to the left panel
  const handleCategoryHover = (catId: string, rowEl: HTMLButtonElement) => {
    setActiveCategoryId(catId)
    if (leftPanelRef.current) {
      const panelTop  = leftPanelRef.current.getBoundingClientRect().top
      const rowTop    = rowEl.getBoundingClientRect().top
      setRightPanelTop(rowTop - panelTop)
    }
  }

  const openTs = () => {
    if (tsCloseTimer.current) clearTimeout(tsCloseTimer.current)
    setTsOpen(true)
  }

  const scheduleTsClose = () => {
    tsCloseTimer.current = setTimeout(() => {
      setTsOpen(false)
      setActiveTsId(null)
      setTsRightPanelTop(0)
    }, 150)
  }

  const closeTs = () => {
    setTsOpen(false)
    setActiveTsId(null)
    setTsRightPanelTop(0)
  }

  const handleTsCategoryHover = (catId: string, rowEl: HTMLButtonElement) => {
    setActiveTsId(catId)
    if (tsLeftPanelRef.current) {
      const panelTop = tsLeftPanelRef.current.getBoundingClientRect().top
      const rowTop = rowEl.getBoundingClientRect().top
      setTsRightPanelTop(rowTop - panelTop)
    }
  }

  const openPractice = () => {
    if (practiceCloseTimer.current) clearTimeout(practiceCloseTimer.current)
    setPracticeOpen(true)
  }

  const schedulePracticeClose = () => {
    practiceCloseTimer.current = setTimeout(() => {
      setPracticeOpen(false)
    }, 150)
  }

  const closePractice = () => {
    setPracticeOpen(false)
  }

  if (variant !== 'marketing') return null

  const hasResources = categories.length > 0

  // Only resolve when explicitly hovered — no fallback to categories[0]
  const activeCategory = activeCategoryId
    ? (categories.find(c => c.id === activeCategoryId) ?? null)
    : null

  const activeTsCategory = activeTsId
    ? (TEST_SERIES_CATEGORIES.find(c => c.id === activeTsId) ?? null)
    : null

  return (
    <>
      {/* Scroll progress bar */}
      <div
        className="fixed top-0 left-0 right-0 h-[3px] z-[100] origin-left bg-gradient-to-r from-[#1a6fd4] to-[#0ea5e9]"
        style={{ transform: `scaleX(${scrollProgress})`, transition: 'transform 0.1s linear' }}
      />

      <nav
        className={`fixed top-[3px] left-0 right-0 z-50 transition-transform duration-500 ${
          showNavbar ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className={`transition-all duration-300 ${
          isScrolled
            ? 'bg-white/90 backdrop-blur-xl shadow-md border-b border-gray-200/60'
            : 'bg-white/70 backdrop-blur-sm'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-[68px] items-center justify-between gap-6">

              {/* Logo */}
              <Link href="/" className="flex-shrink-0 flex items-center group">
                <div className="relative h-12 w-[160px] transition-transform duration-300 group-hover:scale-105">
                  <Image src="/logo.png" alt="Mockzy" fill className="object-contain object-left" priority />
                </div>
              </Link>

              {/* Desktop nav */}
              <div className="hidden md:flex items-center gap-1">
                {/* ── Test Series mega-menu ── */}
                <div
                  className="relative"
                  ref={tsRef}
                  onMouseEnter={openTs}
                  onMouseLeave={scheduleTsClose}
                >
                  <Link
                    href="/test-series"
                    className={`relative flex items-center gap-1.5 px-4 py-2 text-[15px] font-medium rounded-lg transition-all duration-200 group ${
                      tsOpen
                        ? 'text-[#1a6fd4] bg-blue-50'
                        : 'text-gray-700 hover:text-[#1a6fd4] hover:bg-blue-50'
                    }`}
                  >
                    <span className={`transition-colors ${tsOpen ? 'text-[#1a6fd4]' : 'text-gray-400 group-hover:text-[#1a6fd4]'}`}>
                      <BookOpen className="h-4 w-4" />
                    </span>
                    Test Series
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${tsOpen ? 'rotate-180 text-[#1a6fd4]' : 'text-gray-400'}`} />
                  </Link>

                  {tsOpen && (
                    <>
                      {/* Invisible bridge */}
                      <div className="absolute left-0 top-full w-full h-2" aria-hidden="true" />

                      <div className="absolute left-0 top-full mt-2 z-50">
                        {/* Left column */}
                        <div
                          ref={tsLeftPanelRef}
                          className="w-56 bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden py-3"
                        >
                          {TEST_SERIES_CATEGORIES.map((cat) => {
                            const isActive = cat.id === activeTsId
                            return (
                              <button
                                key={cat.id}
                                onMouseEnter={(e) => handleTsCategoryHover(cat.id, e.currentTarget)}
                                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors text-left ${
                                  isActive
                                    ? 'text-[#1a6fd4] bg-blue-50'
                                    : 'text-gray-700 hover:text-[#1a6fd4] hover:bg-blue-50/60'
                                }`}
                              >
                                {cat.name}
                                {isActive && (
                                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[#1a6fd4]" />
                                )}
                              </button>
                            )
                          })}
                          <div className="border-t border-gray-100 my-1 pt-1">
                            <Link href="/test-series" onClick={closeTs} className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-[#1a6fd4] hover:bg-blue-50/60 transition-colors">
                              <span>All Test Series Packages</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                          </div>
                        </div>

                        {/* Right panel */}
                        {activeTsCategory && (
                          <div
                            className="absolute left-full top-0 ml-1 min-w-[200px] bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden py-3 px-2"
                            style={{ top: tsRightPanelTop }}
                          >
                            <p className="px-3 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 border-b border-gray-100 mb-2">
                              Select Class
                            </p>
                            {activeTsCategory.classes.map((cls) => (
                              <Link
                                key={cls.slug}
                                href={`/test-series/${activeTsCategory.id}/${cls.slug}`}
                                onClick={closeTs}
                                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-700 hover:text-[#1a6fd4] hover:bg-blue-50 transition-colors"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-gray-300 group-hover:bg-[#1a6fd4]" />
                                {cls.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* ── Courses mega-menu / link ── */}
                <div
                  className="relative"
                  ref={coursesRef}
                  onMouseEnter={() => {
                    if (coursesCloseTimer.current) clearTimeout(coursesCloseTimer.current)
                    setCoursesOpen(true)
                  }}
                  onMouseLeave={() => {
                    coursesCloseTimer.current = setTimeout(() => {
                      setCoursesOpen(false)
                    }, 150)
                  }}
                >
                  <Link
                    href="/courses"
                    className={`relative flex items-center gap-1.5 px-4 py-2 text-[15px] font-medium rounded-lg transition-all duration-200 group ${
                      coursesOpen
                        ? 'text-[#1a6fd4] bg-blue-50'
                        : 'text-gray-700 hover:text-[#1a6fd4] hover:bg-blue-50'
                    }`}
                  >
                    <span className={`transition-colors ${coursesOpen ? 'text-[#1a6fd4]' : 'text-gray-400 group-hover:text-[#1a6fd4]'}`}>
                      <Video className="h-4 w-4" />
                    </span>
                    Courses
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${coursesOpen ? 'rotate-180 text-[#1a6fd4]' : 'text-gray-400'}`} />
                  </Link>

                  {coursesOpen && (
                    <>
                      <div className="absolute left-0 top-full w-full h-2" aria-hidden="true" />
                      <div className="absolute left-0 top-full mt-2 z-50 w-64 bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden py-3 px-2">
                        <div className="px-3 pb-2 border-b border-gray-100 flex items-center justify-between">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Live Batches</span>
                          <span className="text-[10px] font-semibold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">🔴 Zoom</span>
                        </div>
                        <div className="mt-1 space-y-0.5">
                          {coursesList.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-gray-400">Batches enrolling now</div>
                          ) : (
                            coursesList.slice(0, 5).map((c) => (
                              <Link
                                key={c.id}
                                href={`/courses/${c.slug}`}
                                onClick={() => setCoursesOpen(false)}
                                className="flex flex-col px-3 py-2 rounded-xl text-sm text-gray-700 hover:text-[#1a6fd4] hover:bg-blue-50 transition-colors"
                              >
                                <span className="font-semibold text-xs line-clamp-1">{c.title}</span>
                                <span className="text-[11px] text-gray-500 font-medium">₹{c.monthlyPrice}/month • {c.targetExam || 'Live'}</span>
                              </Link>
                            ))
                          )}
                          <Link
                            href="/courses"
                            onClick={() => setCoursesOpen(false)}
                            className="flex items-center justify-between px-3 py-2 mt-1 rounded-xl text-xs font-bold text-[#1a6fd4] bg-blue-50/60 hover:bg-blue-50 transition-colors"
                          >
                            <span>Explore All Batches</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* ── PYQ mega-menu ── */}
                <div
                  className="relative"
                  ref={pyqRef}
                  onMouseEnter={() => {
                    if (pyqCloseTimer.current) clearTimeout(pyqCloseTimer.current)
                    setPyqOpen(true)
                  }}
                  onMouseLeave={() => {
                    pyqCloseTimer.current = setTimeout(() => {
                      setPyqOpen(false)
                      setActivePyqId(null)
                      setPyqRightPanelTop(0)
                    }, 150)
                  }}
                >
                  <Link
                    href="/pyq"
                    className={`relative flex items-center gap-1.5 px-4 py-2 text-[15px] font-medium rounded-lg transition-all duration-200 group ${
                      pyqOpen
                        ? 'text-[#1a6fd4] bg-blue-50'
                        : 'text-gray-700 hover:text-[#1a6fd4] hover:bg-blue-50'
                    }`}
                  >
                    <span className={`transition-colors ${pyqOpen ? 'text-[#1a6fd4]' : 'text-gray-400 group-hover:text-[#1a6fd4]'}`}>
                      <FileText className="h-4 w-4" />
                    </span>
                    PYQ
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${pyqOpen ? 'rotate-180 text-[#1a6fd4]' : 'text-gray-400'}`} />
                  </Link>

                  {pyqOpen && (
                    <>
                      {/* Invisible bridge */}
                      <div className="absolute left-0 top-full w-full h-2" aria-hidden="true" />

                      <div className="absolute left-0 top-full mt-2 z-50">
                        {/* Left column */}
                        <div
                          ref={pyqLeftPanelRef}
                          className="w-56 bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden py-3"
                        >
                          {pyqCategories.length === 0 ? (
                            <p className="px-4 py-2 text-sm text-gray-500">No categories</p>
                          ) : pyqCategories.map((cat) => {
                            const isActive = cat.id === activePyqId
                            return (
                              <Link
                                key={cat.id}
                                href={`/pyq/${cat.slug}`}
                                onMouseEnter={(e) => {
                                  setActivePyqId(cat.id)
                                  if (pyqLeftPanelRef.current) {
                                    const panelTop = pyqLeftPanelRef.current.getBoundingClientRect().top
                                    const rowTop = e.currentTarget.getBoundingClientRect().top
                                    setPyqRightPanelTop(rowTop - panelTop)
                                  }
                                }}
                                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors text-left ${
                                  isActive
                                    ? 'text-[#1a6fd4] bg-blue-50'
                                    : 'text-gray-700 hover:text-[#1a6fd4] hover:bg-blue-50/60'
                                }`}
                                onClick={() => {
                                  setPyqOpen(false)
                                }}
                              >
                                {cat.name}
                              </Link>
                            )
                          })}
                          <div className="border-t border-gray-100 my-1 pt-1">
                            <Link href="/pyq" onClick={() => setPyqOpen(false)} className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-[#1a6fd4] hover:bg-blue-50/60 transition-colors">
                              <span>All Solved PYQs</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* ── Practice mega-menu ── */}
                <div
                  className="relative"
                  ref={practiceRef}
                  onMouseEnter={openPractice}
                  onMouseLeave={schedulePracticeClose}
                >
                  <Link
                    href="/questions"
                    className={`relative flex items-center gap-1.5 px-4 py-2 text-[15px] font-medium rounded-lg transition-all duration-200 group ${
                      practiceOpen
                        ? 'text-[#1a6fd4] bg-blue-50'
                        : 'text-gray-700 hover:text-[#1a6fd4] hover:bg-blue-50'
                    }`}
                  >
                    <span className={`transition-colors ${practiceOpen ? 'text-[#1a6fd4]' : 'text-gray-400 group-hover:text-[#1a6fd4]'}`}>
                      <PenLine className="h-4 w-4" />
                    </span>
                    Practice Questions
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${practiceOpen ? 'rotate-180 text-[#1a6fd4]' : 'text-gray-400'}`} />
                  </Link>

                  {practiceOpen && (
                    <>
                      {/* Invisible bridge */}
                      <div className="absolute left-0 top-full w-full h-2" aria-hidden="true" />

                      <div className="absolute left-0 top-full mt-2 z-50">
                          <div
                            className="w-56 bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden py-3"
                          >
                          {pyqCategories.map((cat) => {
                            return (
                              <Link
                                key={cat.id}
                                href={`/practice-exams?exam=${cat.slug}`}
                                onClick={closePractice}
                                className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors text-left text-gray-700 hover:text-[#1a6fd4] hover:bg-blue-50/60"
                              >
                                {cat.name}
                              </Link>
                            )
                          })}
                          <div className="border-t border-gray-100 my-1 pt-1">
                            <Link href="/questions" onClick={closePractice} className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-[#1a6fd4] hover:bg-blue-50/60 transition-colors">
                              <span>Browse Question Bank (Free)</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* ── Resources mega-menu ── */}
                {hasResources && (
                  <div
                    className="relative"
                    ref={resourcesRef}
                    onMouseEnter={openMenu}
                    onMouseLeave={scheduleClose}
                  >
                    {/* Trigger button */}
                    <Link
                      href="/study-materials"
                      className={`relative flex items-center gap-1.5 px-4 py-2 text-[15px] font-medium rounded-lg transition-all duration-200 group ${
                        resourcesOpen
                          ? 'text-[#1a6fd4] bg-blue-50'
                          : 'text-gray-700 hover:text-[#1a6fd4] hover:bg-blue-50'
                      }`}
                    >
                      <span className={`transition-colors ${resourcesOpen ? 'text-[#1a6fd4]' : 'text-gray-400 group-hover:text-[#1a6fd4]'}`}>
                        <Layers className="h-4 w-4" />
                      </span>
                      Study Materials
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${resourcesOpen ? 'rotate-180 text-[#1a6fd4]' : 'text-gray-400'}`} />
                    </Link>

                    {resourcesOpen && (
                      <>
                        {/* Invisible bridge: covers the gap between button and panel */}
                        <div className="absolute left-0 top-full w-full h-2" aria-hidden="true" />

                        {/* Outer wrapper — left panel only, positioned normally */}
                        <div className="absolute left-0 top-full mt-2 z-50">

                          {/* Left column: category list */}
                          <div
                            ref={leftPanelRef}
                            className="w-48 bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden py-3"
                          >
                            {categories.map((cat) => {
                              const isActive = cat.id === activeCategoryId
                              return (
                                <button
                                  key={cat.id}
                                  onMouseEnter={(e) => handleCategoryHover(cat.id, e.currentTarget)}
                                  className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors text-left ${
                                    isActive
                                      ? 'text-[#1a6fd4] bg-blue-50'
                                      : 'text-gray-700 hover:text-[#1a6fd4] hover:bg-blue-50/60'
                                  }`}
                                >
                                  {cat.name}
                                  {isActive && (
                                    <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[#1a6fd4]" />
                                  )}
                                </button>
                              )
                            })}
                          </div>

                          {/* Right panel: pages, positioned dynamically to align with hovered row */}
                          {activeCategory && (
                            <div
                              className="absolute left-full top-0 ml-1 min-w-[240px] bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden py-3 px-2"
                              style={{ top: rightPanelTop }}
                            >
                              {activeCategory.navGroups.map((group, gi) => {
                                if (group.pages.length === 0) return null
                                return (
                                  <div key={gi} className="mb-2 last:mb-0">
                                    {group.name && (
                                      <p className="px-3 pt-1 pb-0.5 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                                        {group.name}
                                      </p>
                                    )}
                                    {group.pages.map((page) => (
                                      <Link
                                        key={page.id}
                                        href={`/resources/${activeCategory.slug}/${page.slug}`}
                                        onClick={closeMenu}
                                        className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-700 hover:text-[#1a6fd4] hover:bg-blue-50 transition-colors"
                                      >
                                        <FileText className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                                        <span>{page.title}</span>
                                      </Link>
                                    ))}
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop auth */}
              <div className="hidden md:flex items-center gap-3">
                {status === 'loading' ? (
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-20 rounded-lg bg-gray-100 animate-pulse" />
                    <div className="h-9 w-28 rounded-lg bg-gray-100 animate-pulse" />
                  </div>
                ) : session?.user ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-50 border border-gray-200">
                      {session.user.image ? (
                        <img src={session.user.image} alt={session.user.name || 'User'} className="w-7 h-7 rounded-full ring-2 ring-[#1a6fd4]/40" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-[#1a6fd4] flex items-center justify-center text-white text-sm font-bold">
                          {session.user.name?.charAt(0) ?? 'U'}
                        </div>
                      )}
                      <div className="flex flex-col justify-center">
                        <p className="text-sm font-semibold text-gray-900 leading-none">{session.user.name?.split(' ')[0]}</p>
                        {session.user.role && (
                          <p className="text-[11px] text-gray-500 flex items-center gap-0.5 mt-0.5 leading-none capitalize">
                            {session.user.role === 'admin' && <Crown className="h-3 w-3" />}
                            {session.user.role}
                          </p>
                        )}
                      </div>
                    </div>
                    <Link href={session.user.role === 'admin' ? '/admin/dashboard' : '/dashboard'}>
                      <Button size="sm" className="bg-[#1a6fd4] hover:bg-[#1558b0] text-white shadow-sm">
                        Dashboard <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </Button>
                    </Link>
                    <Button size="sm" variant="ghost" onClick={() => signOut({ callbackUrl: '/' })} className="text-gray-500 hover:text-gray-800">
                      Sign out
                    </Button>
                  </div>
                ) : (
                  <>
                    <Link href="/login">
                      <Button size="sm" variant="ghost" className="text-gray-700 hover:text-[#1a6fd4] font-medium">
                        <LogIn className="mr-1.5 h-4 w-4" /> Login
                      </Button>
                    </Link>
                    <Link href="/login">
                      <Button size="sm" className="relative overflow-hidden bg-[#1a6fd4] hover:bg-[#1558b0] text-white font-semibold px-5 shadow-md shadow-blue-200 hover:shadow-blue-300 transition-all duration-300 group">
                        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                        <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                        <span className="relative">Get Started</span>
                      </Button>
                    </Link>
                  </>
                )}
              </div>

              {/* Mobile hamburger */}
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon" aria-label="Toggle menu">
                    <div className="relative w-5 h-5">
                      <Menu className={`h-5 w-5 absolute inset-0 transition-all duration-300 ${isOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`} />
                      <X    className={`h-5 w-5 absolute inset-0 transition-all duration-300 ${isOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`} />
                    </div>
                  </Button>
                </SheetTrigger>

                <SheetContent side="right" className="w-full sm:w-[360px] p-0 flex flex-col">
                  <SheetHeader className="px-6 py-5 border-b border-gray-100">
                    <SheetTitle className="flex items-center gap-3">
                      <div className="relative h-10 w-[130px]">
                        <Image src="/logo.png" alt="Mockzy" fill className="object-contain object-left" />
                      </div>
                    </SheetTitle>
                  </SheetHeader>

                  <div className="flex-1 overflow-y-auto px-4 py-6 space-y-1">
                    {NAV_LINKS.map((link) => (
                      <Link key={link.label} href={link.href} onClick={() => setIsOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-blue-50 hover:text-[#1a6fd4] transition-all">
                        <span className="text-gray-400">{link.icon}</span>
                        {link.label}
                      </Link>
                    ))}

                    {/* Mobile Test Series */}
                    <div>
                      <button
                        onClick={() => setMobileTsOpen(!mobileTsOpen)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-blue-50 hover:text-[#1a6fd4] transition-all"
                      >
                        <span className="text-gray-400"><BookOpen className="h-4 w-4" /></span>
                        Test Series
                        <ChevronDown className={`h-4 w-4 ml-auto text-gray-400 transition-transform duration-200 ${mobileTsOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {mobileTsOpen && (
                        <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-blue-100 pl-4">
                          {TEST_SERIES_CATEGORIES.map((cat) => {
                            const isExpanded = mobileActiveTsId === cat.id
                            return (
                              <div key={cat.id}>
                                <button
                                  onClick={() => setMobileActiveTsId(isExpanded ? null : cat.id)}
                                  className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:text-[#1a6fd4] hover:bg-blue-50 transition-colors"
                                >
                                  {cat.name}
                                  <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                </button>

                                {isExpanded && (
                                  <div className="ml-2 mt-1 space-y-1">
                                    <p className="px-3 pt-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">Classes</p>
                                    {cat.classes.map((cls) => (
                                      <Link
                                        key={cls.slug}
                                        href={`/test-series/${cat.id}/${cls.slug}`}
                                        onClick={() => setIsOpen(false)}
                                        className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-600 hover:text-[#1a6fd4] hover:bg-blue-50 transition-colors"
                                      >
                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                                        <span>{cls.name}</span>
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                          <Link href="/test-series" onClick={() => setIsOpen(false)} className="flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold text-[#1a6fd4] bg-blue-50/60 mt-2">View All Test Series Packages →</Link>
                        </div>
                      )}
                    </div>

                    {/* Mobile Courses */}
                    <Link
                      href="/courses"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-blue-50 hover:text-[#1a6fd4] transition-all"
                    >
                      <span className="text-gray-400"><Video className="h-4 w-4" /></span>
                      Courses & Batches
                      <span className="ml-auto text-[10px] font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-100">
                        🔴 Zoom
                      </span>
                    </Link>

                    {/* Mobile PYQ */}
                    <div>
                      <button
                        onClick={() => setMobilePyqOpen(!mobilePyqOpen)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-blue-50 hover:text-[#1a6fd4] transition-all"
                      >
                        <span className="text-gray-400"><FileText className="h-4 w-4" /></span>
                        PYQ
                        <ChevronDown className={`h-4 w-4 ml-auto text-gray-400 transition-transform duration-200 ${mobilePyqOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {mobilePyqOpen && (
                        <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-blue-100 pl-4">
                          {pyqCategories.length === 0 ? (
                            <p className="px-3 py-2 text-xs text-gray-500">No categories</p>
                          ) : pyqCategories.map((cat) => (
                            <div key={cat.id}>
                              <Link
                                href={`/pyq/${cat.slug}`}
                                onClick={() => setIsOpen(false)}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:text-[#1a6fd4] hover:bg-blue-50 transition-colors"
                              >
                                {cat.name}
                              </Link>
                            </div>
                          ))}
                          <Link href="/pyq" onClick={() => setIsOpen(false)} className="flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold text-[#1a6fd4] bg-blue-50/60 mt-2">All PYQ Solved Papers →</Link>
                        </div>
                      )}
                    </div>

                    {/* Mobile Practice */}
                    <div>
                      <button
                        onClick={() => setMobilePracticeOpen(!mobilePracticeOpen)}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-blue-50 hover:text-[#1a6fd4] transition-all"
                      >
                        <span className="text-gray-400"><PenLine className="h-4 w-4" /></span>
                        Practice Questions
                        <ChevronDown className={`h-4 w-4 ml-auto text-gray-400 transition-transform duration-200 ${mobilePracticeOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {mobilePracticeOpen && (
                        <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-blue-100 pl-4">
                          {pyqCategories.map((cat) => {
                            return (
                              <Link
                                key={cat.id}
                                href={`/practice-exams?exam=${cat.slug}`}
                                onClick={() => setIsOpen(false)}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:text-[#1a6fd4] hover:bg-blue-50 transition-colors"
                              >
                                {cat.name}
                              </Link>
                            )
                          })}
                          <Link href="/questions" onClick={() => setIsOpen(false)} className="flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold text-[#1a6fd4] bg-blue-50/60 mt-2">Browse Question Bank (Free) →</Link>
                        </div>
                      )}
                    </div>
                    {/* Mobile Resources — tap category to expand its pages */}
                    {hasResources && (
                      <div>
                        <button
                          onClick={() => setMobileResOpen(!mobileResOpen)}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-blue-50 hover:text-[#1a6fd4] transition-all"
                        >
                          <span className="text-gray-400"><Layers className="h-4 w-4" /></span>
                          Resources
                          <ChevronDown className={`h-4 w-4 ml-auto text-gray-400 transition-transform duration-200 ${mobileResOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {mobileResOpen && (
                          <div className="ml-4 mt-1 space-y-0.5 border-l-2 border-blue-100 pl-4">
                            <Link href="/study-materials" onClick={() => setIsOpen(false)} className="flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold text-[#1a6fd4] bg-blue-50/60 mb-2">Free Study Materials Hub →</Link>
                            {categories.map((cat) => {
                              const catPages = cat.navGroups.flatMap(g => g.pages)
                              if (catPages.length === 0) return null
                              const isExpanded = mobileActiveCatId === cat.id
                              return (
                                <div key={cat.id}>
                                  <button
                                    onClick={() => setMobileActiveCatId(isExpanded ? null : cat.id)}
                                    className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:text-[#1a6fd4] hover:bg-blue-50 transition-colors"
                                  >
                                    {cat.name}
                                    <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                  </button>

                                  {isExpanded && cat.navGroups.map((group, gi) => {
                                    if (group.pages.length === 0) return null
                                    return (
                                      <div key={gi} className="ml-2">
                                        {group.name && (
                                          <p className="px-3 pt-1 text-[10px] font-bold uppercase tracking-widest text-gray-400">{group.name}</p>
                                        )}
                                        {group.pages.map((page) => (
                                          <Link
                                            key={page.id}
                                            href={`/resources/${cat.slug}/${page.slug}`}
                                            onClick={() => setIsOpen(false)}
                                            className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-gray-600 hover:text-[#1a6fd4] hover:bg-blue-50 transition-colors"
                                          >
                                            <FileText className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                                            <span className="truncate">{page.title}</span>
                                          </Link>
                                        ))}
                                      </div>
                                    )
                                  })}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Mobile auth */}
                  <div className="border-t border-gray-100 px-4 py-5 space-y-3">
                    {status === 'loading' ? (
                      <>
                        <div className="h-11 rounded-xl bg-gray-100 animate-pulse" />
                        <div className="h-11 rounded-xl bg-gray-100 animate-pulse" />
                      </>
                    ) : session?.user ? (
                      <>
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
                          {session.user.image ? (
                            <img src={session.user.image} alt={session.user.name || 'User'} className="w-10 h-10 rounded-full ring-2 ring-[#1a6fd4]/40" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-[#1a6fd4] flex items-center justify-center text-white font-bold text-lg">
                              {session.user.name?.charAt(0) ?? 'U'}
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-900">{session.user.name}</p>
                            {session.user.role && (
                              <p className="text-xs text-gray-400 flex items-center gap-1">
                                {session.user.role === 'admin' && <Crown className="h-3 w-3" />}
                                {session.user.role}
                              </p>
                            )}
                          </div>
                        </div>
                        <Link href={session.user.role === 'admin' ? '/admin/dashboard' : '/dashboard'} onClick={() => setIsOpen(false)}>
                          <Button className="w-full bg-[#1a6fd4] hover:bg-[#1558b0] text-white">Go to Dashboard</Button>
                        </Link>
                        <Button variant="ghost" className="w-full text-gray-500" onClick={() => { setIsOpen(false); signOut({ callbackUrl: '/' }) }}>
                          Sign Out
                        </Button>
                      </>
                    ) : (
                      <>
                        <Link href="/login" onClick={() => setIsOpen(false)}>
                          <Button variant="outline" className="w-full border-gray-300">
                            <LogIn className="mr-2 h-4 w-4" /> Login
                          </Button>
                        </Link>
                        <Link href="/login" onClick={() => setIsOpen(false)}>
                          <Button className="w-full bg-[#1a6fd4] hover:bg-[#1558b0] text-white font-semibold">
                            <Sparkles className="mr-2 h-4 w-4" /> Get Started Free
                          </Button>
                        </Link>
                      </>
                    )}
                  </div>
                </SheetContent>
              </Sheet>

            </div>
          </div>
        </div>
      </nav>

      {/* Spacer */}
      <div className="h-[71px]" />
    </>
  )
}